<template>
  <div class="login-content">
    <Form ref="form" v-slot="{ meta }" @submit="submitLogin">
      <div class="mb-2">
        <div class="form__input mb-4">
          <p class="input-label mb-2">{{ $t('all_account') }}</p>
          <Field v-slot="{ field, errorMessage, handleChange, validate }" v-model="email" name="email" :rules="formRules.email">
            <v-text-field
              :model-value="field.value"
              :placeholder="$t('account_hint')"
              :min-height="40"
              :color="'orange50'"
              :error-messages="errorMessage"
              :hide-details="errorMessage?.length === 0"
              variant="outlined"
              density="compact"
              required
              @update:model-value="handleChange"
              @blur="validate"
            />
          </Field>
        </div>

        <div class="form__input">
          <p class="input-label mb-2">{{ $t('all_pwd') }}</p>
          <Field v-slot="{ field, errorMessage, handleChange, validate }" v-model="password" name="password" :rules="formRules.password">
            <v-text-field
              :model-value="field.value"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('pwd_hint')"
              :color="'orange50'"
              :min-height="40"
              :error-messages="errorMessage"
              hide-details="auto"
              variant="outlined"
              density="compact"
              required
              @update:model-value="handleChange"
              @blur="validate"
            >
              <template #append-inner>
                <div @click="showPassword = !showPassword">
                  <funnow-icon class="dna-icon wd-w-5" max-width="20" :image-path="`dna-icon-eyes-${showPassword ? 'close' : 'open'}.svg`" />
                </div>
              </template>
            </v-text-field>
          </Field>
        </div>
      </div>

      <div class="pb-6 d-flex justify-end">
        <v-btn class="forget-password__btn" :height="24" variant="text" @click.stop="changeForgotPasswordPage">
          {{ $t('forgeot_pwd') }}
        </v-btn>
      </div>

      <dialog-error v-model:show-status="hasError" :error="error" />

      <v-btn
        class="login__btn mt-3 mb-5"
        :class="{ disabled: disabledLoginBtn || !meta.valid }"
        :height="40"
        type="submit"
        color="orange50"
        variant="flat"
        block
      >
        <loading v-if="loginLoading" />
        <span v-else> {{ $t('all_login') }} </span>
      </v-btn>
    </Form>

    <div class="dialog-footer text-center">
      <div class="question-hint">
        <span> {{ $t('login_new_user') }} </span>
        <span class="login-text wd-text-orange50" @click="changeRegisterPage"> {{ $t('create_account') }} </span>
      </div>

      <v-divider class="my-5" />

      <v-btn class="other-way__btn" :height="24" color="orange50" variant="text" @click="changeIndexPage">
        {{ $t('login_other_method') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { useGtm } from '@/composition/useGtm'
import { GtmEventMap } from '@/view-models/gtm'

import Loading from '@/components/icons/loading.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import DialogError from '@/components/notice/error/dialog-error.vue'

export default {
  name: 'LoginIndex',
  components: {
    Loading,
    FunnowIcon,
    DialogError,
  },
  emits: ['changeAuthStep', 'success'],
  setup() {
    const { sendGtmEvent } = useGtm()
    const rules = useFieldRules()
    const formRules = computed(() => ({
      email: rules.email(),
      password: rules.password(),
    }))

    return {
      sendGtmEvent,
      formRules,
    }
  },
  data() {
    return {
      loginLoading: false,
      showPassword: false,
      disableBtn: true,
      email: '',
      password: '',
      hasError: false,
      error: {},
    }
  },
  computed: {
    disabledLoginBtn() {
      return this.loginLoading
    },
  },
  methods: {
    clearError() {
      this.hasError = false
      this.error = {}
    },
    changeIndexPage() {
      this.$emit('changeAuthStep', 'index')
    },
    changeForgotPasswordPage() {
      this.$emit('changeAuthStep', 'forgot-password')
    },
    changeRegisterPage() {
      this.$emit('changeAuthStep', 'sign-up')
    },
    getLoginData() {
      return {
        username: this.email,
        password: this.password,
      }
    },
    async submitLogin() {
      const { valid } = await this.$refs.form.validate()

      if (valid && !this.loginLoading) {
        this.loginLoading = true
        this.dispatchLogin()
      }
    },
    dispatchLogin() {
      this.clearError()
      const loginData = this.getLoginData()
      this.$store
        .dispatch('authentication/login', loginData)
        .then(res => {
          this.loginLoading = false
          if (res.code === 0) {
            const { uid } = res.data
            this.sendTrackingCode(uid)
            this.sendGtmEvent({
              event: GtmEventMap.ACCOUNT_LOGGED_IN,
              payload: {
                'Login Type': 'Email',
              },
            })

            this.$emit('success')
          }
        })
        .catch(error => {
          this.loginLoading = false
          this.hasError = true
          this.error = error
          console.error(error)
        })
    },
    sendTrackingCode(UID) {
      if (UID) {
        this.$gtm.push({
          event: 'login',
          authenticationMethod: 'email',
          userId: UID,
        })
      }
    },
  },
}
</script>
<style scoped lang="scss">
.login-content {
  max-height: calc(75vh - 146px);
  overflow-y: auto;
  .form-block {
    .input-label {
      font-size: 13px;
    }
  }

  .login__btn {
    font-size: 17px;
    &.disabled {
      background-color: $orange30 !important;
      pointer-events: none;
    }
  }

  .forget-password__btn {
    padding: 0;
    font-size: 13px;
  }

  .dialog-footer {
    .question-hint {
      line-height: 24px;
      .login-text {
        cursor: pointer;
      }
    }
    .question-hint,
    .other-way__btn {
      font-size: 13px;
    }
  }

  .dna-icon {
    margin-top: 2px;
    cursor: pointer;
  }
}
</style>
